import { OverlayViewF, useGoogleMap } from '@react-google-maps/api'
import { rem } from 'polished'
import React, { MouseEventHandler, useCallback, useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'

import MapInfoWindow from './MapInfoWindow'
import PinHeart from './PinAccessory/PinHeart'
import SelectedMapItemPin from './Pins/SelectedMapItemPin'
import TeardropPin, { TEARDROP_PIN_HEIGHT } from './Pins/TeardropPin'
import MapSummaryCard from '../SummaryCards'
import {
  BaseMapElementComponentProps,
  panToIntoViewIfObstructed,
  pinOffSetter,
  startPlaceOfTripItem,
} from '../utils'

import Clickable from 'components/Common/Clickable'
import { ItemMetadata } from 'tripPlanner/models'
import { TripTab } from 'tripPlanner/utils/tabs'
import { MarkerItem } from 'tripPlanner/types/tripItem'
import { itemSectionId } from 'tripPlanner/utils/itemSection'
import { TripMapContext } from 'tripPlanner/contexts/TripMapContext'

const StyledClickable = styled(Clickable)`
  position: absolute;
  left: 0;
  bottom: 0;
  transform: translateX(-50%);
`

const Container = styled.div`
  position: relative;
`

const PinHeartContainer = styled.div`
  position: absolute;
  bottom: ${rem(24)};
  left: ${rem(17)};
  z-index: 9999;
`

interface Props extends BaseMapElementComponentProps {
  item: MarkerItem
  enableWindow?: boolean
  itemCardRef: React.MutableRefObject<HTMLDivElement | null>
  tab: TripTab
}

function TripItemMapMarker({
  active,
  item,
  position,
  zIndex,
  itemCardRef,
  tab,
}: Props) {
  const Icon = ItemMetadata[item.type].icon

  const map = useGoogleMap()
  const windowRef = useRef<HTMLDivElement | null>(null)
  const { onSelectMapItem, onDeselectMapItem } = useContext(TripMapContext)

  const onClick: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (active) {
        onDeselectMapItem()
      } else {
        onSelectMapItem(itemSectionId(item.id))
      }
    },
    [active, onDeselectMapItem, onSelectMapItem, item.id],
  )

  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (ref.current && google?.maps) {
      google.maps.OverlayView.preventMapHitsFrom(ref.current)
    }
  }, [ref])

  useEffect(() => {
    if (active && map && windowRef.current) {
      // We should only really pan into position if its obstructed?
      panToIntoViewIfObstructed(map, position, itemCardRef.current)
    }
  }, [active, itemCardRef, map, position])

  const name = startPlaceOfTripItem(item)?.name ?? ''

  return (
    <OverlayViewF
      zIndex={active ? 999 : zIndex}
      position={position}
      css={{ pointerEvents: 'none' }}
      getPixelPositionOffset={pinOffSetter}
      mapPaneName="overlayMouseTarget"
    >
      <Container ref={ref}>
        <StyledClickable onClick={onClick}>
          {tab === 'Explore' && !active && (
            <PinHeartContainer>
              <PinHeart />
            </PinHeartContainer>
          )}
          {active && <SelectedMapItemPin text={name} />}
          {!active && (
            <TeardropPin isActive={false}>
              <Icon colour="neutral-eight" />
            </TeardropPin>
          )}
        </StyledClickable>

        <MapInfoWindow
          ref={windowRef}
          open={active}
          alignment="center"
          position="above"
          style={{ top: -TEARDROP_PIN_HEIGHT * 1.3 }}
        >
          <MapSummaryCard item={item} />
        </MapInfoWindow>
      </Container>
    </OverlayViewF>
  )
}

export default React.memo(TripItemMapMarker)
